import { FC } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    useUpdateContractorMutation,
} from '../../../store/api/contractors-api'
import { routes } from '../../../constants/routes'
import PageLayout from '../../../components/page-layout/page-layout'
import HelpDrawerForm from '../components/help-drawer/help-drawer-form'
import ClientForm, { ClientFormValues } from '../components/clients-form'
import { QueryParams } from '../../../types/strapi/strapi'
import { useGetContractorsTypesQuery } from '../../../store/api/contractor-types-api'
import useErrorModal from '../../../hooks/api/use-error-modal'
import { ApprovalStatus } from '../../../types/api/contractor'
import { useAppSelector } from '../../../store/store'
import { BusinessRole } from '../../../types/api/user'
import {
    useCreateContractorContactMutation,
    useUpdateContractorContactMutation,
} from '../../../store/api/contractor-contacts-api'
import { syncContacts, buildUpdateContractorData } from '../helpers/client-operations'
import { useScopedContractorsQuery } from '../../../hooks/api/use-scoped-contractors-query'

interface ClientsEditPageProps {}

const ClientsEditPage: FC<ClientsEditPageProps> = () => {
    const { docId } = useParams<{ docId: string }>()
    const navigate = useNavigate()
    const user = useAppSelector(state => state.auth.user)
    const isLogist = user?.businessRoles?.includes(BusinessRole.Logist)

    const { data, isLoading: isContractorLoading } = useScopedContractorsQuery({
        filters: { documentId: { $eq: docId } },
        populate: { curators: { populate: { user: true } }, contractor_contacts: true },
    })
    const params: QueryParams = {
        filters: { name: { $eq: 'Клиент' } },
    }

    const { data: typesData, isLoading: isTypesLoading } = useGetContractorsTypesQuery(params)
    const clientTypeId = typesData?.data?.find(t => t.name === 'Клиент')?.documentId

    const [updateContractor, updateContractorState] = useUpdateContractorMutation()
    const [createContact, createContactState] = useCreateContractorContactMutation()
    const [updateContact, updateContactState] = useUpdateContractorContactMutation()

    useErrorModal(updateContractorState, 'Не удалось обновить клиента')
    useErrorModal(createContactState, 'Не удалось создать контакт клиента')
    useErrorModal(updateContactState, 'Не удалось обновить контакт клиента')

    if (!data?.data?.length) return null
    const contractor = data.data[0]

    const isApproved = contractor.approvalStatus === 'approved'
    const canEdit = isLogist || !isApproved

    const onFinish = async (values: ClientFormValues) => {
        if (!docId || !clientTypeId) return

        const contactDocIds = await syncContacts(values, {
            createContact: async data => createContact(data).unwrap(),
            updateContact: async params => updateContact(params).unwrap(),
        })

        const updateData = buildUpdateContractorData({
            values,
            clientTypeId,
            contactDocIds,
            existingContractor: contractor,
        })

        await updateContractor({
            docId,
            body: { data: updateData },
        }).unwrap()

        navigate(routes.clients)
    }

    return (
        <PageLayout title={'Редактирование клиента'} scrollableContent beforeUser={<HelpDrawerForm />}>
            <ClientForm
                initialValues={{
                    name: contractor.name,
                    approvalStatus: (contractor.approvalStatus || 'draft') as ApprovalStatus,
                    legalAddress: contractor.legalAddress,
                    defermentInDays: contractor.defermentInDays,
                    source: contractor.source,
                    exhibitionName: contractor.exhibitionName,
                    comment: contractor.comment,
                    curators:
                        contractor.curators?.map(curator => ({
                            user: curator.user?.documentId,
                            percent: curator.percent,
                        })) || [],
                    contacts:
                        contractor.contractor_contacts?.map(contact => ({
                            documentId: contact.documentId,
                            address: contact.address,
                            pointName: contact.pointName,
                            receiverName: contact.receiverName,
                            phone: contact.phone,
                            workingHours: contact.workingHours,
                            latCoordinates: contact.latCoordinates,
                            longCoordinates: contact.longCoordinates,
                            comment: contact.comment,
                            isPrimary: contact.isPrimary ?? undefined,
                        })) || [],
                }}
                onFinish={onFinish}
                loading={isContractorLoading || isTypesLoading}
                submitText='Сохранить изменения'
                disabled={!canEdit}
            />
        </PageLayout>
    )
}

export default ClientsEditPage
